/**
 * Shared date/time display helpers (PBI #1106).
 *
 * Contract:
 * - **UTC instants** (API `*At`, `*AtUtc`, `eventTimestamp`, ISO with Z/offset):
 *   use {@link formatInstantLocal} / {@link formatInstantLocalShort} /
 *   {@link formatInstantLocalDate} so the viewer sees browser-local time
 *   (never invent a fixed product TZ). For sorts/diffs use
 *   {@link parseApiUtcInstant} / {@link compareApiUtcInstants} — never raw
 *   `new Date(apiString)`.
 * - **Calendar dates** (`YYYY-MM-DD` send/delivery/arrival with no time):
 *   use {@link formatCalendarDate} so the civil day does not shift by TZ.
 *
 * Prefer these helpers over ad-hoc `dayjs(...).format('MM/DD/YYYY h:mm A')`
 * or raw `toLocaleString` copies in display code.
 *
 * Why {@link parseApiUtcInstant} exists
 * ------------------------------------
 * EF Core + SQL `datetime2` often round-trip as `DateTimeKind.Unspecified`.
 * System.Text.Json then emits `2026-07-23T22:13:00` **without** `Z`. ES
 * `new Date(that)` treats offset-less forms as **local**, so a true UTC
 * instant is shifted by the browser offset (e.g. 4:13 PM MDT stored as
 * 22:13 UTC displays as 10:13 PM). LocalDB makes this easy to spot; Azure
 * can show the same bug. We only append `Z` for date-time shapes — never
 * for civil `YYYY-MM-DD` (see {@link formatCalendarDate}).
 *
 * Do **not** globally force every API `DateTime` to UTC-with-Z on the server:
 * civil fields like `ScheduledDate` are Unspecified midnights and would shift
 * a day when shown as instants. Stamp known UTC projections with
 * `DateTimeKind.Utc` (or use `DateTimeOffset`) at the DTO boundary instead.
 */

/**
 * Parses an API UTC instant. Offset-less ISO date-times are treated as UTC.
 */
export const parseApiUtcInstant = (iso: string): Date => {
  const trimmed = iso.trim();

  // Civil date-only — caller should use formatCalendarDate; do not force Z.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }

  const hasOffset = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
  if (
    !hasOffset &&
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(trimmed)
  ) {
    const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    return new Date(`${normalized}Z`);
  }

  return new Date(trimmed);
};

/**
 * Epoch ms for an API UTC instant string, or NaN when unparseable.
 * Use for sorting/filtering instead of `new Date(apiField).getTime()`.
 */
export const apiUtcInstantMs = (iso: string | null | undefined): number => {
  if (!iso) return Number.NaN;
  return parseApiUtcInstant(iso).getTime();
};

/**
 * Comparator for API UTC instant strings (ascending). Nulls sort last.
 */
export const compareApiUtcInstants = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const aMs = apiUtcInstantMs(a);
  const bMs = apiUtcInstantMs(b);
  const aOk = !Number.isNaN(aMs);
  const bOk = !Number.isNaN(bMs);
  if (!aOk && !bOk) return 0;
  if (!aOk) return 1;
  if (!bOk) return -1;
  return aMs - bMs;
};

/**
 * Formats an API UTC instant as a local calendar date (no time).
 * Returns '--' for null/empty.
 */
export const formatInstantLocalDate = (iso: string | null | undefined): string => {
  if (!iso) return '--';
  const d = parseApiUtcInstant(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

/**
 * Formats an API UTC instant as local date + time (browser zone implied;
 * no zone suffix — keeps dense admin tables aligned). Returns '--' for null/empty.
 */
export const formatInstantLocal = (iso: string | null | undefined): string => {
  if (!iso) return '--';
  const d = parseApiUtcInstant(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Compact local date + time for dense admin tables (no seconds, no zone name).
 */
export const formatInstantLocalShort = (iso: string | null | undefined): string => {
  if (!iso) return '--';
  const d = parseApiUtcInstant(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Formats a civil calendar date (`YYYY-MM-DD` or date-only ISO prefix) without
 * applying a timezone shift. Parses year/month/day components directly.
 */
export const formatCalendarDate = (yyyyMmDdOrIsoDate: string | null | undefined): string => {
  if (!yyyyMmDdOrIsoDate) return '--';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(yyyyMmDdOrIsoDate.trim());
  if (!match) {
    // Fall back to instant parsing only when the string is not date-only shaped.
    return formatInstantLocalDate(yyyyMmDdOrIsoDate);
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const d = new Date(year, monthIndex, day);
  if (Number.isNaN(d.getTime())) return '--';

  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};
